import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface InAppNotification {
  id: string;
  title: string;
  body: string;
  type: string;
  data: any;
  timestamp: string;
  read: boolean;
}

export interface OrderConfirmationModal {
  visible: boolean;
  orderData: any | null;
  confirmationType: 'pending' | null;
  title: string;
  body: string;
}

interface NotificationState {
  inAppNotifications: InAppNotification[];
  unreadCount: number;
  lastNotification: InAppNotification | null;
  isLoading: boolean;
  error: string | null;
  orderConfirmationModal: OrderConfirmationModal;
  audioInitModal: {
    visible: boolean;
    promptCount: number; // Track how many times we've shown the modal
  };
}

const initialState: NotificationState = {
  inAppNotifications: [],
  unreadCount: 0,
  lastNotification: null,
  isLoading: false,
  error: null,
  orderConfirmationModal: {
    visible: false,
    orderData: null,
    confirmationType: null,
    title: '',
    body: '',
  },
  audioInitModal: {
    visible: false,
    promptCount: 0,
  },
};

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    addInAppNotification: (state, action: PayloadAction<InAppNotification>) => {
      const incoming = action.payload;
      const incomingOrderId =
        incoming.data?.order_id ||
        incoming.data?.orderId ||
        incoming.data?.order_number ||
        incoming.data?.order_code;
      const incomingStatus = incoming.data?.status || '';
      const incomingType = incoming.type || incoming.data?.type || '';
      const duplicateIndex = state.inAppNotifications.findIndex((notification) => {
        const existingOrderId =
          notification.data?.order_id ||
          notification.data?.orderId ||
          notification.data?.order_number ||
          notification.data?.order_code;
        const existingStatus = notification.data?.status || '';
        const existingType = notification.type || notification.data?.type || '';

        if (incomingOrderId && existingOrderId) {
          return (
            String(existingOrderId) === String(incomingOrderId) &&
            String(existingStatus) === String(incomingStatus) &&
            String(existingType) === String(incomingType)
          );
        }

        return notification.id === incoming.id;
      });

      if (duplicateIndex !== -1) {
        state.inAppNotifications[duplicateIndex] = {
          ...state.inAppNotifications[duplicateIndex],
          ...incoming,
          read: state.inAppNotifications[duplicateIndex].read && incoming.read,
        };
        state.lastNotification = state.inAppNotifications[duplicateIndex];
        return;
      }

      state.inAppNotifications.unshift(action.payload);
      state.lastNotification = action.payload;
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.inAppNotifications.find(n => n.id === action.payload);
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead: (state) => {
      state.inAppNotifications.forEach(notification => {
        notification.read = true;
      });
      state.unreadCount = 0;
    },
    incrementUnreadCount: (state) => {
      state.unreadCount += 1;
    },
    decrementUnreadCount: (state) => {
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    },
    setOrderConfirmationModal: (state, action: PayloadAction<Partial<OrderConfirmationModal>>) => {
      state.orderConfirmationModal = { ...state.orderConfirmationModal, ...action.payload };
    },
    clearOrderConfirmationModal: (state) => {
      state.orderConfirmationModal = {
        visible: false,
        orderData: null,
        confirmationType: null,
        title: '',
        body: '',
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearAllNotifications: (state) => {
      state.inAppNotifications = [];
      state.unreadCount = 0;
      state.lastNotification = null;
    },
    showAudioInitModal: (state) => {
      state.audioInitModal.visible = true;
      state.audioInitModal.promptCount += 1;
    },
    hideAudioInitModal: (state) => {
      state.audioInitModal.visible = false;
    },
  },
});

export const {
  addInAppNotification,
  markAsRead,
  markAllAsRead,
  incrementUnreadCount,
  decrementUnreadCount,
  setOrderConfirmationModal,
  clearOrderConfirmationModal,
  setLoading,
  setError,
  clearAllNotifications,
  showAudioInitModal,
  hideAudioInitModal,
} = notificationSlice.actions;

export default notificationSlice.reducer;
